import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Autocomplete, Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material'
import React from 'react'
import type { ClienteDTO, SiniestroStatus } from '../../../model/kartera-api-model'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { personName, RamoMark, selectMenu, SINIESTRO_STATUS } from '../common/claim-common'
import { DESCRIPCION_MAX, useClaimFormModel } from './claim-form-model'

const checkboxSx = { color: 'rgb(var(--k-muted))', '&.Mui-checked': { color: 'rgb(var(--k-primary))' } }

const Summary = ({ label, children }: { label: string; children?: React.ReactNode }) => (
    <div>
        <dt className="mb-0.5 text-xs text-k-muted">{label}</dt>
        <dd className="m-0 break-words text-sm font-semibold text-k-ink">{children || '—'}</dd>
    </div>
)

const ClaimForm = () => {
    const model = useClaimFormModel()
    const { form, change, poliza, clienteLookup } = model

    return (
        <KarteraAdminTemplate loading={false} title={model.title}>
            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.loading && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description={model.isEdit ? 'Actualiza el seguimiento del siniestro.' : 'Registra el siniestro sobre una póliza vigente del cliente.'}
                    backTo={model.backTo}
                    backLabel="Siniestros"
                    actions={<span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Póliza afectada</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <Autocomplete<ClienteDTO>
                            options={clienteLookup.options}
                            loading={clienteLookup.searching}
                            value={form.cliente}
                            inputValue={clienteLookup.inputValue}
                            onInputChange={(_, next, reason) => clienteLookup.changeInput(next, reason)}
                            onChange={(_, option) => change.cliente(option)}
                            getOptionLabel={(option) => personName(option) || ''}
                            isOptionEqualToValue={(option, current) => option.id === current.id}
                            filterOptions={(items) => items}
                            noOptionsText={clienteLookup.noOptionsText}
                            loadingText="Buscando..."
                            slotProps={{ paper: { className: 'v2-select-menu' } }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    className="v2-field"
                                    variant="filled"
                                    fullWidth
                                    required
                                    label="Cliente"
                                    helperText="Busca por nombre (mín. 3 letras)."
                                />
                            )}
                        />
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth
                            select
                            required
                            label="Póliza"
                            value={form.polizaId === '' ? '' : String(form.polizaId)}
                            disabled={!form.cliente || model.loadingPolizas}
                            helperText={model.polizaHelp}
                            onChange={(event) => change.poliza(event.target.value ? Number(event.target.value) : '')}
                            slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                        >
                            <MenuItem value=""><em>Seleccione</em></MenuItem>
                            {model.polizas.map((item) => (
                                <MenuItem key={item.id} value={String(item.id)}>
                                    <span className="flex items-center gap-2">
                                        <RamoMark ramo={item.ramo} />
                                        <span className="tabular-nums">{item.noPoliza || '—'}</span>
                                        {item.aseguradora && <span className="text-k-muted">({item.aseguradora})</span>}
                                    </span>
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>

                    {poliza && (
                        <div className="mt-5 flex flex-col gap-4 rounded-2xl border border-k-line bg-k-bg px-[18px] py-4">
                            <div className="flex items-center gap-3">
                                <RamoMark ramo={model.ramo} boxed />
                                <div className="flex min-w-0 flex-col">
                                    <span className="truncate text-sm font-extrabold text-k-ink">Póliza {poliza.noPoliza || '—'}</span>
                                    <span className="truncate text-xs text-k-muted">{model.ramo || 'Sin ramo'}</span>
                                </div>
                            </div>
                            <dl className="m-0 grid grid-cols-1 gap-x-[18px] gap-y-3 sm:grid-cols-3">
                                <Summary label="Contratante">{model.contratante}</Summary>
                                <Summary label="Concepto">{poliza.concepto}</Summary>
                                <Summary label="Aseguradora">
                                    {model.aseguradora}{poliza.agenteClave?.clave ? ` (${poliza.agenteClave.clave})` : ''}
                                </Summary>
                            </dl>

                            {model.showAsegurados && (
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-[13px] font-bold text-k-ink">Asegurados involucrados</span>
                                    {form.asegurados.length === 0 && <p className="m-0 text-sm italic text-k-danger-ink">La póliza no tiene asegurados registrados.</p>}
                                    {form.asegurados.map((item) => (
                                        <FormControlLabel
                                            key={item.id}
                                            sx={{ mx: 0, color: 'rgb(var(--k-ink))', '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                                            control={(
                                                <Checkbox
                                                    size="small"
                                                    checked={item.selected}
                                                    sx={checkboxSx}
                                                    onChange={(event) => change.asegurado(item.id, event.target.checked)}
                                                />
                                            )}
                                            label={`${item.name || '—'}${item.parentesco ? ` (${item.parentesco})` : ''}`}
                                        />
                                    ))}
                                    {form.asegurados.length > 0 && <span className="text-xs text-k-muted">Selecciona al menos un asegurado.</span>}
                                </div>
                            )}
                            {model.vehiculo && (
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-[13px] font-bold text-k-ink">Vehículo</span>
                                    <span className="text-sm text-k-ink-2">{model.vehiculoLabel || '—'}{model.vehiculo.placas ? ` · Placas ${model.vehiculo.placas}` : ''}</span>
                                </div>
                            )}
                            {model.missingVehiculo && (
                                <p className="m-0 text-sm italic text-k-danger-ink">No se ha registrado un vehículo en la póliza.</p>
                            )}
                            {model.bienTexto && (
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-[13px] font-bold text-k-ink">Diversos / daños</span>
                                    <p className="m-0 whitespace-pre-line text-sm text-k-ink-2">{model.bienTexto}</p>
                                </div>
                            )}
                        </div>
                    )}
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Datos del siniestro</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <TextField
                            className="v2-field" variant="filled" fullWidth
                            required
                            label="No. de siniestro"
                            value={form.noSiniestro}
                            onChange={(event) => change.noSiniestro(event.target.value)}
                            slotProps={{ htmlInput: { style: { fontVariantNumeric: 'tabular-nums' } } }}
                        />
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth
                            select
                            required
                            label="Estatus"
                            value={form.status}
                            onChange={(event) => change.status(event.target.value as SiniestroStatus)}
                            slotProps={{ select: selectMenu }}
                        >
                            {SINIESTRO_STATUS.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)}
                        </TextField>
                        <TextField
                            className="v2-field" variant="filled" fullWidth
                            required
                            label="Fecha del siniestro"
                            type="date"
                            value={form.fechaSiniestro}
                            onChange={(event) => change.fechaSiniestro(event.target.value)}
                        />
                        <TextField
                            className="v2-field" variant="filled" fullWidth
                            required
                            label="Fecha estimada de resolución"
                            type="date"
                            value={form.fechaEstimadaResolucion}
                            error={model.resolucionAntes}
                            helperText={model.resolucionAntes ? 'No puede ser anterior a la fecha del siniestro' : undefined}
                            onChange={(event) => change.fechaEstimadaResolucion(event.target.value)}
                            slotProps={{ htmlInput: { min: form.fechaSiniestro || undefined } }}
                        />
                        <TextField
                            className="v2-field md:col-span-2" variant="filled" fullWidth
                            required
                            multiline
                            minRows={5}
                            label="Descripción"
                            value={form.descripcion}
                            helperText={`${form.descripcion.length}/${DESCRIPCION_MAX}`}
                            onChange={(event) => change.descripcion(event.target.value)}
                        />
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.loading || model.loadingPoliza}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar siniestro
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default ClaimForm
